import React, { useMemo, useState, useEffect } from "react";
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  Select,
  message,
  Typography,
  Upload,
  Checkbox,
  Divider,
  Steps,
  DatePicker,
  InputNumber,
  Descriptions,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { Header, Footer } from "components/Layout";
import { API_CONFIG } from "./config";
import { apiFetch } from "../utils/apiFetch";

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;

const DRAFT_KEY = "apply:draft:v1";

export default function Apply() {
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [savingDraft, setSavingDraft] = useState(false);
  const [form] = Form.useForm();
  const [docs, setDocs] = useState({
    idCopy: null,
    proofOfIncome: null,
    bankStatements: [],
    proofOfResidence: null,
    consentForm: null,
  });

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const makeUploadProps = (key, multi = false) => ({
    beforeUpload: async (file) => {
      const base64 = await toBase64(file);
      setDocs((prev) => ({
        ...prev,
        [key]: multi
          ? [
              ...(prev[key] || []),
              { name: file.name, type: file.type, size: file.size, base64 },
            ]
          : {
              name: file.name,
              type: file.type,
              size: file.size,
              base64,
            },
      }));
      return false; // prevent auto upload
    },
    onRemove: () => {
      setDocs((prev) => ({ ...prev, [key]: multi ? [] : null }));
    },
    maxCount: multi ? 5 : 1,
    accept: ".pdf,.png,.jpg,.jpeg",
  });

  const productDefault = useMemo(() => {
    const p = params.get("product");
    if (p === "indigent") return "indigent";
    if (p === "lending") return "lending";
    return "lending";
  }, [params]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.values) form.setFieldsValue(draft.values);
      if (draft?.docs) setDocs(draft.docs);
    } catch {}
  }, [form]);

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const values = form.getFieldsValue();
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ values, docs }));
      message.success("Draft saved");
    } catch {
      message.error("Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const moneyParser = (v) => {
    const n = String(v || "").replace(/[^\d.]/g, "");
    return n ? Number(n) : 0;
  };

  const monthlyIncome = Form.useWatch("monthlyIncome", form);
  const otherIncome = Form.useWatch("otherIncome", form);
  const monthlyExpenses = Form.useWatch("monthlyExpenses", form);
  const existingRepayments = Form.useWatch("existingRepayments", form);
  const affordability =
    moneyParser(monthlyIncome) +
    moneyParser(otherIncome) -
    moneyParser(monthlyExpenses) -
    moneyParser(existingRepayments);

  const stepFields = [
    ["productType", "applicantName", "identityNumber", "dateOfBirth", "phone", "email", "country", "address"],
    ["employmentStatus", "employerName", "monthlyIncome", "otherIncome"],
    ["monthlyExpenses", "existingRepayments", "amount", "purpose"],
    ["consent"],
  ];

  const goNext = async () => {
    try {
      await form.validateFields(stepFields[currentStep]);
      if (currentStep === 2) {
        if (!docs.idCopy || !docs.proofOfIncome || (docs.bankStatements || []).length === 0 || !docs.proofOfResidence || !docs.consentForm) {
          message.error("Please upload all required documents.");
          return;
        }
      }
      setCurrentStep((s) => Math.min(s + 1, 4));
    } catch {}
  };

  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const onFinish = async (values) => {
    setSubmitting(true);
    setResult(null);
    try {
      if (!docs.idCopy || !docs.proofOfIncome || (docs.bankStatements || []).length === 0 || !docs.proofOfResidence || !docs.consentForm) {
        message.error("Please upload all required documents.");
        setSubmitting(false);
        return;
      }
      const payload = {
        ...values,
        tenantId: localStorage.getItem("tenantId") || localStorage.getItem("mockdb:tenantId") || 1,
        channel: "SELF",
        documents: docs,
      };
      const response = await apiFetch(API_CONFIG.submitApplicationUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "Failed to submit");
      setResult(data);
      localStorage.removeItem(DRAFT_KEY);
      message.success(`Application submitted: ${data.applicationId}`);
    } catch (err) {
      message.error(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="layout-default layout-apply">
      <Header btn="primary" />
      <Content className="apply-content">
        <div className="apply-shell">
          <Card className="apply-card" bordered={false}>
          <div className="apply-grid">
            <aside className="apply-rail">
              <div className="apply-rail-title">Application Journey</div>
              <Steps current={currentStep} direction="vertical" className="apply-steps">
                <Step title="Applicant" />
                <Step title="Income" />
                <Step title="Expenses" />
                <Step title="Documents" />
                <Step title="Review" />
              </Steps>
              <div className="apply-rail-meta">
                Estimated time: 6-8 minutes · Auto-saved drafts
              </div>
            </aside>
            <div className="apply-form">
              <Title level={3}>Start your application</Title>
              <Paragraph className="apply-sub">
                Complete the form below to submit your application. You will be able to track the status afterwards.
              </Paragraph>
              <Form layout="vertical" form={form} onFinish={onFinish} initialValues={{ productType: productDefault }}>
            {currentStep === 0 && (
            <>
            <Form.Item label="Product" name="productType" rules={[{ required: true, message: "Select a product" }]}>
              <Select>
                <Option value="lending">Micro-Lending</Option>
                <Option value="indigent">Indigent Support</Option>
              </Select>
            </Form.Item>
            <Divider className="apply-divider">Applicant Information</Divider>
            <Form.Item label="Full Name" name="applicantName" rules={[{ required: true, message: "Enter your name" }]}>
              <Input placeholder="Your full name" />
            </Form.Item>
            <Form.Item label="ID Number" name="identityNumber" rules={[{ required: true, message: "Enter your ID number" }]}>
              <Input placeholder="ID Number" />
            </Form.Item>
            <Form.Item label="Date of Birth" name="dateOfBirth" rules={[{ required: true, message: "Enter your date of birth" }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Phone" name="phone">
              <Input placeholder="Phone number" />
            </Form.Item>
            <Form.Item label="Email" name="email">
              <Input placeholder="Email address" />
            </Form.Item>
            <Form.Item label="Country" name="country" initialValue="South Africa">
              <Input placeholder="Country" />
            </Form.Item>
            <Form.Item label="Residential Address" name="address" rules={[{ required: true, message: "Enter your address" }]}>
              <Input placeholder="Street, City, Postal code" />
            </Form.Item>
            </>
            )}

            {currentStep === 1 && (
            <>
            <Divider className="apply-divider">Employment and Income</Divider>
            <Form.Item label="Employment Status" name="employmentStatus" rules={[{ required: true, message: "Select your employment status" }]}>
              <Select>
                <Option value="employed">Employed</Option>
                <Option value="self-employed">Self-employed</Option>
                <Option value="unemployed">Unemployed</Option>
                <Option value="student">Student</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Employer Name" name="employerName">
              <Input placeholder="Employer name" />
            </Form.Item>
            <Form.Item label="Monthly Net Income" name="monthlyIncome" rules={[{ required: true, message: "Enter monthly income" }]}>
              <InputNumber style={{ width: "100%" }} formatter={(v) => `R ${v}`} parser={moneyParser} />
            </Form.Item>
            <Form.Item label="Other Income (optional)" name="otherIncome">
              <InputNumber style={{ width: "100%" }} formatter={(v) => `R ${v}`} parser={moneyParser} />
            </Form.Item>
            </>
            )}

            {currentStep === 2 && (
            <>
            <Divider className="apply-divider">Living Expenses</Divider>
            <Form.Item label="Monthly Living Expenses" name="monthlyExpenses" rules={[{ required: true, message: "Enter monthly expenses" }]}>
              <InputNumber style={{ width: "100%" }} formatter={(v) => `R ${v}`} parser={moneyParser} />
            </Form.Item>
            <Form.Item label="Existing Credit Repayments" name="existingRepayments" rules={[{ required: true, message: "Enter credit repayments" }]}>
              <InputNumber style={{ width: "100%" }} formatter={(v) => `R ${v}`} parser={moneyParser} />
            </Form.Item>

            <Divider className="apply-divider">Loan Details</Divider>
            <Form.Item label="Requested Amount" name="amount">
              <InputNumber style={{ width: "100%" }} formatter={(v) => `R ${v}`} parser={moneyParser} />
            </Form.Item>
            <Form.Item label="Purpose of Loan" name="purpose">
              <Input placeholder="Purpose (optional)" />
            </Form.Item>
            <div className={`affordability ${affordability < 0 ? "bad" : "ok"}`}>
              Estimated affordability: <strong>R {affordability || 0}</strong>
            </div>
            </>
            )}

            {currentStep === 3 && (
            <>
            <Divider className="apply-divider">Required Documents</Divider>
            <div className="doc-checklist">
              <div className={docs.idCopy ? "doc-ok" : "doc-miss"}>ID Copy</div>
              <div className={docs.proofOfIncome ? "doc-ok" : "doc-miss"}>Proof of Income</div>
              <div className={(docs.bankStatements || []).length ? "doc-ok" : "doc-miss"}>Bank Statements</div>
              <div className={docs.proofOfResidence ? "doc-ok" : "doc-miss"}>Proof of Residence</div>
              <div className={docs.consentForm ? "doc-ok" : "doc-miss"}>Consent Form</div>
            </div>
            <Form.Item label="Certified ID Copy" required>
              <Upload {...makeUploadProps("idCopy")}>
                <Button icon={<UploadOutlined />}>Upload ID Copy</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="Proof of Income (Payslip / Proof of Revenue)" required>
              <Upload {...makeUploadProps("proofOfIncome")}>
                <Button icon={<UploadOutlined />}>Upload Proof of Income</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="Bank Statements (Last 3 months)" required>
              <Upload {...makeUploadProps("bankStatements", true)}>
                <Button icon={<UploadOutlined />}>Upload Bank Statements</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="Proof of Residence" required>
              <Upload {...makeUploadProps("proofOfResidence")}>
                <Button icon={<UploadOutlined />}>Upload Proof of Residence</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="Consent Form (Signed)" required>
              <Upload {...makeUploadProps("consentForm")}>
                <Button icon={<UploadOutlined />}>Upload Consent</Button>
              </Upload>
            </Form.Item>

            <Divider className="apply-divider">Consent</Divider>
            <Form.Item
              name="consent"
              valuePropName="checked"
              rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error("Consent is required"))) }]}
            >
              <Checkbox>
                I consent to affordability checks, credit bureau checks, and processing of my personal information.
              </Checkbox>
            </Form.Item>
            </>
            )}

            {currentStep === 4 && (
            <>
              <Divider className="apply-divider">Review</Divider>
              <Descriptions bordered column={1} size="small" className="review-box">
                {Object.entries(form.getFieldsValue()).map(([k, v]) => (
                  <Descriptions.Item key={k} label={k}>
                    {String(v ?? "")}
                  </Descriptions.Item>
                ))}
              </Descriptions>
              <Button type="primary" htmlType="submit" loading={submitting} className="cta-primary">
                Submit Application
              </Button>
            </>
            )}
          </Form>
              <div className="step-actions">
                <Button onClick={goBack} disabled={currentStep === 0}>Back</Button>
                <Button onClick={saveDraft} loading={savingDraft}>Save & Continue Later</Button>
                {currentStep < 4 && (
                  <Button type="primary" onClick={goNext}>Next</Button>
                )}
              </div>
              {result && (
                <div className="apply-result">
                  Application ID: <strong>{result.applicationId}</strong> · Status: {result.status}
                </div>
              )}
            </div>
          </div>
          </Card>
        </div>
      </Content>
      <Footer />

      <style>{`
        .layout-apply .apply-content{
          padding: 24px 20px 56px;
          min-height: 100vh;
          color: #f8fafc;
          background:
            radial-gradient(1000px 500px at 10% 10%, rgba(253,224,71,0.12), transparent 60%),
            radial-gradient(1200px 800px at -20% 0%, rgba(253,224,71,0.06), transparent 70%),
            radial-gradient(1000px 600px at 70% 100%, rgba(124,212,255,0.06), transparent 70%),
            linear-gradient(180deg, #0b0b12 0%, #0f1117 100%);
          position: relative;
          overflow: hidden;
        }
        .layout-apply .apply-shell{
          max-width: 860px;
          margin: 0 auto;
        }
        .apply-grid{
          display: grid;
          grid-template-columns: 220px 1fr;
          gap: 24px;
        }
        .apply-rail{
          position: sticky;
          top: 16px;
          align-self: start;
          background: linear-gradient(180deg, rgba(7,11,20,0.9), rgba(12,16,24,0.9));
          border: 1px solid rgba(253,224,71,0.22);
          border-radius: 14px;
          padding: 16px 14px;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
        }
        .apply-rail-title{
          font-weight: 700;
          color: #fde68a;
          margin-bottom: 10px;
        }
        .apply-rail-meta{
          margin-top: 12px;
          font-size: 12px;
          color: rgba(248,250,252,0.65);
        }
        .apply-steps{
          margin: 0;
        }
        .affordability{
          margin-top: 10px;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(253,224,71,0.2);
          background: rgba(253,224,71,0.06);
          color: #f8fafc;
          font-weight: 600;
        }
        .affordability.bad{ border-color: rgba(248,113,113,0.5); color: #fecaca; }
        .doc-checklist{ display:flex; flex-wrap:wrap; gap:10px; margin-bottom: 12px; }
        .doc-ok, .doc-miss{
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid rgba(253,224,71,0.35);
          font-size: 12px;
        }
        .doc-ok{ color:#fde68a; background: rgba(253,224,71,0.10); }
        .doc-miss{ color:#fca5a5; border-color: rgba(248,113,113,0.4); background: rgba(248,113,113,0.08); }
        .review-box{ margin-bottom: 16px; }
        .step-actions{
          display:flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 14px;
        }
        .apply-card{
          position: relative;
          background:
            radial-gradient(100% 80% at 0% 0%, rgba(201,171,99,0.22), transparent 60%),
            radial-gradient(110% 70% at 100% 100%, rgba(176,139,211,0.20), transparent 60%),
            linear-gradient(180deg, rgba(11,18,32,0.85), rgba(12,16,24,0.85));
          border: 1px solid rgba(201,171,99,0.28);
          border-radius: 16px;
          box-shadow: 0 18px 44px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.05);
          overflow: hidden;
        }
        .apply-card::after{
          content:"";
          position:absolute; inset:0;
          border-radius:16px;
          padding:1px;
          background: linear-gradient(120deg, #C9AB63, #B08BD3, #7CD4FF, #C9AB63);
          background-size:300% 300%;
          animation: applyHue 10s linear infinite;
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events:none;
          opacity:.9;
        }
        @keyframes applyHue { to { background-position: 300% 0; } }
        .apply-sub{ color: rgba(248,250,252,0.75); }
        .apply-divider{
          color:#fde68a;
          border-color: rgba(253,224,71,0.24);
          font-weight: 600;
        }
        .apply-card .ant-form-item-label > label{
          color:#E8D7A8;
          font-weight:600;
        }
        .layout-apply .ant-input,
        .layout-apply .ant-input-affix-wrapper,
        .layout-apply .ant-input-password,
        .layout-apply .ant-select-selector{
          background: linear-gradient(180deg, rgba(14,20,36,0.78), rgba(11,17,30,0.78)) !important;
          border-color: rgba(147,197,253,0.30) !important;
          color: #e6edf7 !important;
          border-radius: 12px !important;
          min-height: 44px;
        }
        .layout-apply .ant-input::placeholder{ color: rgba(226,232,240,0.55); }
        .layout-apply .ant-select-arrow{ color:#c3d8ff; }
        .layout-apply .ant-input-affix-wrapper:hover,
        .layout-apply .ant-input-affix-wrapper-focused,
        .layout-apply .ant-input:focus,
        .layout-apply .ant-select-focused .ant-select-selector{
          border-color: rgba(99,102,241,0.70) !important;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.28), 0 12px 26px rgba(59,130,246,0.16);
        }
        .layout-apply .ant-upload .ant-btn{
          border-radius: 12px;
          background: linear-gradient(180deg, rgba(17,24,39,0.9), rgba(17,24,39,0.75));
          color:#f8fafc;
          border: 1px solid rgba(253,224,71,0.35);
        }
        .layout-apply .ant-upload .ant-btn:hover{
          color:#fde68a;
          border-color:#facc15;
          box-shadow: 0 0 10px rgba(212,175,55,0.35);
        }
        .apply-result{
          margin-top: 16px;
          padding: 12px;
          border-radius: 12px;
          background: rgba(253,224,71,0.08);
          border: 1px solid rgba(253,224,71,0.24);
          color: #f8fafc;
        }
        .cta-primary {
          background: linear-gradient(90deg, #facc15 0%, #eab308 100%);
          border: none;
          color: #111;
          font-weight: 700;
          border-radius: 12px;
          min-height: 46px;
          box-shadow: 0 10px 24px rgba(234,179,8,0.25);
        }
        @media (max-width: 640px){
          .layout-apply .apply-content{ padding: 20px 16px 44px; }
          .apply-grid{ grid-template-columns: 1fr; }
          .apply-rail{ position: static; }
        }
      `}</style>
    </div>
  );
}
